import { useState } from 'react';
import {
  BookOutlined,
  BulbOutlined,
  DeleteOutlined,
  FileAddOutlined,
  FunctionOutlined,
  PlusOutlined,
  SaveOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Badge, Button, Empty, Input, Skeleton, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import type { DinhNghia, GlossaryTerm } from '../../types';
import { GHI_CHU_TOI_DA } from '../../nghiep-vu/ghi-chu';
import { MAU_GHI_CHU_KE_TOAN_VN, MAU_KE_TOAN_VN_CAN_CU, MAU_THUAT_NGU_KE_TOAN_VN } from '../../nghiep-vu/mau-ke-toan-vn';
import { MAU_GHI_CHU_SAP_B1, MAU_SAP_B1_CAN_CU, MAU_THUAT_NGU_SAP_B1, laSapB1 } from '../../nghiep-vu/mau-sap-b1';
import { useDanhSachNguon } from '../../hooks/useChung';
import {
  useDinhNghia,
  useLuuDinhNghia,
  useLuuGhiChu,
  useLuuThuatNgu,
  useThuatNgu,
  useTrangThaiNguon,
} from '../../hooks/useKho';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { DaiLoi } from '../chung/VungDuLieu';

/**
 * Ghi chú nghiệp vụ, từ điển thuật ngữ và định nghĩa nghiệp vụ (port `ThuatNguTab.tsx` native).
 *
 * Thang tín hiệu mạnh dần: **thuật ngữ** giải nghĩa bằng lời; **định nghĩa** mang theo biểu thức SQL; **câu mẫu** mang câu
 * SQL đã chạy được. Không có chỗ ghi lại câu trả lời đúng thì cùng một câu "tổng nguồn vốn 6 tháng" năm lượt ra năm câu
 * lệnh khác nhau. Màn phỏng vấn không thay được chỗ này: nó chỉ hỏi thuộc tính cột, còn "nguồn vốn = chỉ tiêu 440 của bảng
 * cân đối" là tri thức bắc qua nhiều cột, nhiều bảng.
 *
 * Ghi là THAY TRỌN, không phải upsert: màn này luôn gửi toàn bộ những gì nó giữ, và phải tải xong mới cho sửa — gửi danh
 * sách dựng từ bản tải dở là xoá phần chưa tải về.
 */
export function ThuatNguTab({ sourceId }: { sourceId: string }) {
  const { t, locale } = useT();
  const qTn = useThuatNgu(sourceId);
  const qDn = useDinhNghia(sourceId);

  if (qTn.isPending || qDn.isPending) return <Skeleton className="h-64 w-full" />;
  if (qTn.error || qDn.error) {
    return <DaiLoi loi={qTn.error ?? qDn.error} onThuLai={() => void (qTn.error ? qTn.refetch() : qDn.refetch())} />;
  }
  // Đổ bản máy chủ vào state MỘT LẦN mỗi lượt tải (qua `key`): một lần `refetch` giữa chừng không được ném mất phần đang gõ.
  return <ThanTab key={sourceId} sourceId={sourceId} tnGoc={qTn.data ?? []} dnGoc={qDn.data ?? []} t={t} locale={locale} />;
}

function ThanTab({
  sourceId,
  tnGoc,
  dnGoc,
  t,
  locale,
}: {
  sourceId: string;
  tnGoc: GlossaryTerm[];
  dnGoc: DinhNghia[];
  t: ReturnType<typeof useT>['t'];
  locale: string;
}) {
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const nguonQ = useDanhSachNguon();
  const nguon = nguonQ.data?.find((x) => x.id === sourceId);
  // Chỉ để GỢI Ý đúng mẫu, không tự áp: đoán sai thì thừa một nút, tự áp sai thì bản khai mang khẳng định về một lược đồ
  // không tồn tại.
  const trangThai = useTrangThaiNguon(sourceId);
  const coSapB1 = laSapB1((trangThai.data?.tables ?? []).map((x) => x.table));

  const [tn, setTn] = useState<GlossaryTerm[]>(tnGoc);
  const [dn, setDn] = useState<DinhNghia[]>(dnGoc);
  /** `null` = chưa sửa → hiện đúng ghi chú đã lưu của nguồn. */
  const [ghiChuSua, setGhiChuSua] = useState<string | null>(null);
  const ghiChu = ghiChuSua ?? nguon?.ghiChuNghiepVu ?? '';

  const luuGhiChu = useLuuGhiChu(sourceId);
  const luuTn = useLuuThuatNgu(sourceId);
  const luuDn = useLuuDinhNghia(sourceId);
  const loiLuu = (e: unknown) => toast.error(t('troLyAiKhoThuatNgu.luuHong', { loi: moTaLoi(e) }));

  /**
   * Chèn mẫu vào ô, KHÔNG lưu — người vận hành phải đọc, sửa theo hệ thống tài khoản thật rồi mới bấm Lưu. CHÈN THÊM chứ
   * không đè (ô có thể đã mang ghi chú riêng), và thuật ngữ chỉ thêm mục CHƯA có.
   */
  const chenMau = (van: string, thuatNgu: { term: string; meaning: string }[]) => {
    setGhiChuSua(ghiChu.trim() ? `${ghiChu.trim()}\n\n${van}` : van);
    setTn((cu) => {
      const daCo = new Set(cu.map((x) => x.term.trim().toLowerCase()));
      const them = thuatNgu.filter((x) => !daCo.has(x.term.toLowerCase()));
      return them.length ? [...cu, ...them] : cu;
    });
    toast.info(t('troLyAiKhoThuatNgu.daChenMau'));
  };

  return (
    <div className="space-y-4">
      {/* Ghi chú cho cả nguồn đứng ĐẦU: nó là định hướng, ba khối kia là chi tiết — cùng thứ tự với gói ngữ cảnh. */}
      <Tile>
        <TileHeader
          icon={<BulbOutlined />}
          tone="orange"
          title={t('troLyAiKhoThuatNgu.gcTieuDe')}
          mo={t('troLyAiKhoThuatNgu.gcMoTa')}
          action={
            <div className="flex flex-wrap items-center gap-2">
              {/* Mẫu lược đồ chỉ hiện khi nhận ra lược đồ — "chèn mẫu SAP B1" trên nguồn Postgres là mời dán tri thức sai. */}
              {coSapB1 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<FileAddOutlined />}
                  onClick={() => chenMau(MAU_GHI_CHU_SAP_B1, MAU_THUAT_NGU_SAP_B1)}
                >
                  {t('troLyAiKhoThuatNgu.chenMau', { canCu: MAU_SAP_B1_CAN_CU })}
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="ghost"
                icon={<FileAddOutlined />}
                onClick={() => chenMau(MAU_GHI_CHU_KE_TOAN_VN, MAU_THUAT_NGU_KE_TOAN_VN)}
              >
                {t('troLyAiKhoThuatNgu.chenMau', { canCu: MAU_KE_TOAN_VN_CAN_CU })}
              </Button>
              <Button
                size="sm"
                icon={<SaveOutlined />}
                loading={luuGhiChu.isPending}
                onClick={() =>
                  luuGhiChu.mutate(ghiChu.trim(), {
                    onSuccess: () => {
                      toast.success(t('troLyAiKhoThuatNgu.daLuuGhiChu'));
                      setGhiChuSua(null);
                    },
                    onError: loiLuu,
                  })
                }
              >
                {t('troLyAiKhoThuatNgu.luu')}
              </Button>
            </div>
          }
        />
        <TileBody className="space-y-1.5">
          <Textarea
            value={ghiChu}
            maxLength={GHI_CHU_TOI_DA}
            autoSize={{ minRows: 4, maxRows: 20 }}
            placeholder={t('troLyAiKhoThuatNgu.gcViDu')}
            aria-label={t('troLyAiKhoThuatNgu.gcTieuDe')}
            onChange={(e) => setGhiChuSua(e.target.value)}
          />
          {/* Mỗi ký tự ở đây đi vào lời nhắc của MỌI lượt hỏi trên nguồn — nói ra lúc đang gõ. */}
          <p className="text-end text-xs text-content-muted">
            {t('troLyAiKhoThuatNgu.gcDem', { n: soVN(ghiChu.length, locale), tran: soVN(GHI_CHU_TOI_DA, locale) })}
          </p>
        </TileBody>
      </Tile>

      <Tile>
        <TileHeader
          icon={<BookOutlined />}
          tone="violet"
          title={t('troLyAiKhoThuatNgu.tnTieuDe')}
          mo={t('troLyAiKhoThuatNgu.tnMoTa')}
          action={
            <Button
              size="sm"
              icon={<SaveOutlined />}
              loading={luuTn.isPending}
              onClick={() =>
                // Bỏ dòng trống lúc gửi: mục `term` rỗng bị máy chủ lọc trong im lặng.
                luuTn.mutate(
                  tn.filter((x) => x.term.trim()).map((x) => ({ term: x.term.trim(), meaning: x.meaning.trim() })),
                  {
                    onSuccess: (r) => {
                      setTn(r);
                      toast.success(t('troLyAiKhoThuatNgu.daLuuTn', { n: soVN(r.length, locale) }));
                    },
                    onError: loiLuu,
                  },
                )
              }
            >
              {t('troLyAiKhoThuatNgu.luu')}
            </Button>
          }
        />
        <TileBody className="space-y-2">
          {tn.length === 0 ? (
            <Empty title={t('troLyAiKhoThuatNgu.tnRongTieuDe')} description={t('troLyAiKhoThuatNgu.tnRong')} />
          ) : null}
          {tn.map((x, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[14rem_1fr_auto]">
              <Input
                value={x.term}
                placeholder={t('troLyAiKhoThuatNgu.tnThuatNgu')}
                aria-label={t('troLyAiKhoThuatNgu.tnThuatNgu')}
                onChange={(e) => setTn(tn.map((y, j) => (j === i ? { ...y, term: e.target.value } : y)))}
              />
              <Textarea
                value={x.meaning}
                autoSize={{ minRows: 1, maxRows: 6 }}
                placeholder={t('troLyAiKhoThuatNgu.tnNghia')}
                aria-label={t('troLyAiKhoThuatNgu.tnNghia')}
                onChange={(e) => setTn(tn.map((y, j) => (j === i ? { ...y, meaning: e.target.value } : y)))}
              />
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('troLyAiKhoThuatNgu.xoa')}
                title={t('troLyAiKhoThuatNgu.xoa')}
                icon={<DeleteOutlined />}
                onClick={() => setTn(tn.filter((_, j) => j !== i))}
              />
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            icon={<PlusOutlined />}
            onClick={() => setTn([...tn, { term: '', meaning: '' }])}
          >
            {t('troLyAiKhoThuatNgu.them')}
          </Button>
        </TileBody>
      </Tile>

      <Tile>
        <TileHeader
          icon={<FunctionOutlined />}
          tone="teal"
          title={t('troLyAiKhoThuatNgu.dnTieuDe')}
          mo={t('troLyAiKhoThuatNgu.dnMoTa')}
          action={
            <Button
              size="sm"
              icon={<SaveOutlined />}
              loading={luuDn.isPending}
              onClick={() =>
                luuDn.mutate(
                  dn
                    .filter((x) => x.term.trim() && x.sql_expr.trim())
                    .map((x) => ({
                      term: x.term.trim(),
                      sql_expr: x.sql_expr.trim(),
                      depends_on: x.depends_on.map((p) => p.trim()).filter(Boolean),
                    })),
                  {
                    onSuccess: (r) => {
                      setDn(r);
                      toast.success(t('troLyAiKhoThuatNgu.daLuuDn', { n: soVN(r.length, locale) }));
                    },
                    onError: loiLuu,
                  },
                )
              }
            >
              {t('troLyAiKhoThuatNgu.luu')}
            </Button>
          }
        />
        <TileBody className="space-y-3">
          {dn.length === 0 ? (
            <Empty title={t('troLyAiKhoThuatNgu.dnRongTieuDe')} description={t('troLyAiKhoThuatNgu.dnRong')} />
          ) : null}
          {dn.map((x, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-default p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="max-w-[14rem]"
                  value={x.term}
                  placeholder={t('troLyAiKhoThuatNgu.dnThuatNgu')}
                  aria-label={t('troLyAiKhoThuatNgu.dnThuatNgu')}
                  onChange={(e) => setDn(dn.map((y, j) => (j === i ? { ...y, term: e.target.value } : y)))}
                />
                {/* `verified` do NỀN TẢNG đặt (hạ khi lược đồ đổi); màn này chỉ hiện, kèm lý do. */}
                {x.verified === false ? (
                  <Badge variant="warning">
                    <WarningOutlined aria-hidden /> {t('troLyAiKhoThuatNgu.chuaXacNhan')}
                  </Badge>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  className="ms-auto"
                  aria-label={t('troLyAiKhoThuatNgu.xoa')}
                  title={t('troLyAiKhoThuatNgu.xoa')}
                  icon={<DeleteOutlined />}
                  onClick={() => setDn(dn.filter((_, j) => j !== i))}
                />
              </div>
              {x.unverified_reason ? <p className="text-xs text-warning-700">{x.unverified_reason}</p> : null}
              <Textarea
                className="font-mono text-xs"
                value={x.sql_expr}
                autoSize={{ minRows: 2, maxRows: 10 }}
                placeholder={t('troLyAiKhoThuatNgu.dnBieuThuc')}
                aria-label={t('troLyAiKhoThuatNgu.dnBieuThuc')}
                onChange={(e) => setDn(dn.map((y, j) => (j === i ? { ...y, sql_expr: e.target.value } : y)))}
              />
              <Input
                value={x.depends_on.join(', ')}
                placeholder={t('troLyAiKhoThuatNgu.dnPhuThuoc')}
                aria-label={t('troLyAiKhoThuatNgu.dnPhuThuoc')}
                onChange={(e) =>
                  setDn(dn.map((y, j) => (j === i ? { ...y, depends_on: e.target.value.split(',') } : y)))
                }
              />
              <p className="text-xs text-content-muted">{t('troLyAiKhoThuatNgu.dnPhuThuocGhi')}</p>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            icon={<PlusOutlined />}
            onClick={() => setDn([...dn, { term: '', sql_expr: '', depends_on: [] }])}
          >
            {t('troLyAiKhoThuatNgu.them')}
          </Button>
        </TileBody>
      </Tile>
    </div>
  );
}
